import { FC, useEffect } from "react";
import { observer } from "mobx-react";
import { IncreaseShareCapitalPowerOfAttorneyForm } from "./increaseShareCapitalPowerOfAttorneyDomain";
import { NumberQuestion, TextAreaQuestion, YesNoQuestion } from "@/dbot/common/commonComponents";
import { VBox } from "@/components-v2/Box";
import { Checkbox, MCheckboxGroup } from "@/components-v2/Checkbox";
import { CURRENCY_NOK } from "@/utils/currency";

import { useTranslation } from "@/stores/RootStoreContext";
import { RadioFieldQuestion } from "@/common/commonComponents";
import { LocalizedTextEditor } from "@/text/LocalizedTextEditor";
import { findShareTypeName } from "@/common/share";

export const IncreaseShareCapitalPowerOfAttorneyView: FC<{
  form: IncreaseShareCapitalPowerOfAttorneyForm;
}> = observer(({ form }) => {
  const { t, lang } = useTranslation();

  useEffect(() => form.init(), [form]);

  useEffect(() => {
    if (form.shareCapitalReasons) {
      form.shareCapitalReasons.textModels.map((data) => {
        if (!data.model.validatedValue) {
          const text = t("decisions.increaseShareCapitalPoA.shareCapitalReason.text", {
            lang: data.language,
          });
          data.model.setAndCommitInputValue(text);
        }
      });
    }
  }, [form, t]);

  return (
    <VBox gap={"md"}>
      <NumberQuestion
        question={t("decisions.increaseShareCapitalPoA.shareCapitalValue.title")}
        field={form.shareCapitalValue}
        prefix={CURRENCY_NOK}
        infoText={t("decisions.increaseShareCapitalPoA.shareCapitalValue.helperText")}
        isLocked={form.isProjectLocked}
      />
      <RadioFieldQuestion
        question={{ key: "decisions.increaseShareCapitalPoA.shareCapitalDuration.questionText" }}
        field={form.shareCapitalDuration}
        fieldName={"powerOfAttorneyFor"}
        isLocked={form.isProjectLocked}
      />
      {form.isDurationMonths && (
        <NumberQuestion
          question={t("decisions.increaseShareCapitalPoA.shareCapitalDurationMonths.questionText")}
          field={form.shareCapitalDurationMonths}
          onBlur={() => form.setDurationMonthsLimit()}
          infoText={t("decisions.increaseShareCapitalPoA.shareCapitalDurationMonths.helperText")}
          isLocked={form.isProjectLocked}
        />
      )}
      <VBox>
        <div>{t("decisions.increaseShareCapitalPoA.shareClass.title")}</div>
        <MCheckboxGroup
          model={form.shareTypes}
          disabled={form.isProjectLocked}
          labelFn={(shareType) => findShareTypeName(shareType, lang)}
        />
        <Checkbox
          label={t("decisions.increaseShareCapitalPoA.shareClass.newClass")}
          onChange={() => form.isNewShareType.setValue(!form.isNewShareType.value)}
          checked={form.isNewShareType.value}
          disabled={form.isProjectLocked}
        />
      </VBox>
      {form.showNewShareTypeDescriptionInput && (
        <TextAreaQuestion
          question={t(
            "decisions.increaseShareCapitalPoA.shareCapitalNewShareTypeDescription.title",
          )}
          field={form.newShareTypeDescription}
          isLocked={form.isProjectLocked}
        />
      )}
      <hr />
      <YesNoQuestion
        question={t("decisions.increaseShareCapitalPoA.deviateFromShareholderRights")}
        field={form.deviateFromShareholderRights}
        isLocked={form.isProjectLocked}
      />
      <YesNoQuestion
        question={t("decisions.increaseShareCapitalPoA.shareCapitalIncludeOtherProperties")}
        field={form.includeOtherProperties}
        isLocked={form.isProjectLocked}
      />
      <YesNoQuestion
        question={t("decisions.increaseShareCapitalPoA.shareCapitalIncludeConvertingDebt")}
        field={form.includeConvertingDebt}
        isLocked={form.isProjectLocked}
      />
      <YesNoQuestion
        question={t("decisions.increaseShareCapitalPoA.shareCapitalIncludeMerger")}
        field={form.includeMerger}
        isLocked={form.isProjectLocked}
      />
      <LocalizedTextEditor model={form.shareCapitalReasons} hideTranslationOptions />
    </VBox>
  );
});
